import { Application } from 'pixi.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../data/constants';

export type LayoutMode = 'mobile' | 'fullwindow';

// Reads MODE injected by Vite (--mode flag in package scripts).
export function detectLayoutMode(): LayoutMode {
  return import.meta.env.MODE === 'mobile' ? 'mobile' : 'fullwindow';
}

// Target resolution for Pixi Text bitmaps so they stay crisp.
// Mobile (no stage scaling): just DPR is enough.
// Fullwindow (stage scaled to fit window): DPR * stageScale.
export function computeTextResolution(mode: LayoutMode): number {
  const dpr = window.devicePixelRatio || 1;
  const stageScale =
    mode === 'fullwindow'
      ? Math.min(window.innerWidth / DESIGN_WIDTH, window.innerHeight / DESIGN_HEIGHT)
      : 1;
  return Math.max(1, Math.ceil(dpr * stageScale));
}

// Sets up the entire render environment for the chosen mode:
// - tags <body> with the layout class (so CSS rules pick the right styling)
// - creates a Pixi Application sized for the mode
// - mounts the canvas inside #device-frame
// - hooks the resize/fit handler in fullwindow mode
// Returns the ready-to-use Application.
export async function createPixiApp(mode: LayoutMode): Promise<Application> {
  const frame = document.getElementById('device-frame');
  if (!frame) {
    throw new Error('device-frame element not found in index.html');
  }

  document.body.classList.add(`mode-${mode}`);

  const app = new Application();
  await app.init({
    width: mode === 'mobile' ? DESIGN_WIDTH : window.innerWidth,
    height: mode === 'mobile' ? DESIGN_HEIGHT : window.innerHeight,
    background: 0x000000,
    // MSAA leaves a faint diagonal seam along the shared edge of each sprite
    // quad - most visible on large flat sprites (the paper / enemy card on
    // the Fights tab). Off kills the seam; text stays crisp (baked at
    // `resolution`), placeholder Graphics edges lose a little smoothing.
    antialias: false,
    resolution: window.devicePixelRatio || 1,
    autoDensity: true,
    // Use the shared ticker so AnimatedSprite (which subscribes to Ticker.shared
    // by default) is affected by `app.ticker.speed` adjustments. Without this
    // sprite anims play at full 60fps regardless of BATTLE_PACE_SCALE.
    sharedTicker: true,
  });
  frame.appendChild(app.canvas);

  if (mode === 'fullwindow') {
    attachFullwindowFit(app);
  }

  return app;
}

// Fullwindow: canvas covers the WHOLE window; the stage (design rect) is
// fit-scaled and centered via stage.position. Screens that want to paint
// outside the design rect (e.g. the desktop background killing letterbox
// bars) can - they query computeDesignViewport for the visible bounds.
// Scenes that must stay letterboxed (battle) mask themselves to the design
// rect; the canvas background shows through as the bars.
// Returns a teardown that removes the resize listener.
export function attachFullwindowFit(app: Application): () => void {
  const fit = (): void => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const scale = Math.min(w / DESIGN_WIDTH, h / DESIGN_HEIGHT);
    app.renderer.resize(w, h);
    app.stage.scale.set(scale);
    app.stage.position.set(
      (w - DESIGN_WIDTH * scale) / 2,
      (h - DESIGN_HEIGHT * scale) / 2,
    );
  };
  window.addEventListener('resize', fit);
  fit();
  return () => window.removeEventListener('resize', fit);
}

export type DesignViewport = { x: number; y: number; width: number; height: number };

// The visible window area expressed in design coordinates. In fullwindow mode
// the stage is centered, so the visible area extends past the 844x390 design
// rect on one axis (negative x or y). Mobile mode shows exactly the design rect.
export function computeDesignViewport(mode: LayoutMode): DesignViewport {
  if (mode === 'mobile') {
    return { x: 0, y: 0, width: DESIGN_WIDTH, height: DESIGN_HEIGHT };
  }
  const w = window.innerWidth;
  const h = window.innerHeight;
  const scale = Math.min(w / DESIGN_WIDTH, h / DESIGN_HEIGHT);
  return {
    x: (DESIGN_WIDTH - w / scale) / 2,
    y: (DESIGN_HEIGHT - h / scale) / 2,
    width: w / scale,
    height: h / scale,
  };
}
